import React, { useState, useEffect, useMemo } from "react";
import TitleWithIcon from "../TitleWithIcon/TitleWithIcon";
import "./selector.css";
import { Row, Input, Text, Divider, Col } from "../";
import { CheckCircleFilled, LoadingOutlined } from "@ant-design/icons";
import i18n from "../../../../Utils/i18next";
import { deepCopy } from "../../../../Utils/Global";
import { getThemeProperties } from "../../../DashboardPage/getThemeProperties";
import { DELETED_LABEL } from "../../../../config";

const { Search } = Input;

/**
 * Selector component provides a searchable list of items (labels) with selection support.
 *
 * @param {Object} props - Component props
 * @param {Array} props.list - List of items to display
 * @param {Array} props.selecteds - List of currently selected items
 * @param {Function} props.updateList - Function to update the displayed list (often for search)
 * @param {Function} props.select - Function called when an item is selected
 * @param {string|number} props.maxHeight - Maximum height of the scrollable area
 * @param {boolean} props.comesFromDashboardArea - Whether the component is used in the dashboard area
 */
const Selector = ({
  list = [],
  loading = false,
  selecteds = [],
  updateList,
  select,
  maxHeight,
  comesFromDashboardArea,
}) => {
  const [initialList, setInitialList] = useState([]);

  // Initialize initialList once when the list first arrives
  useEffect(() => {
    if (initialList.length === 0 && list.length > 0) {
      setInitialList(deepCopy(list));
    }
  }, [list, initialList.length]);

  /**
   * Detect whether item is selected or not
   * @param {Object} option - Item to check
   */
  const detectSelection = (option) => {
    return selecteds.some((item) => {
      if (item.id === undefined || item.id === null) {
        return item.label === option.id;
      }
      return item.id === option.id;
    });
  };

  /**
   * Filter list based on search value
   * @param {Object} e - Event object
   */
  const onSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();

    if (!searchValue) {
      updateList(deepCopy(initialList));
      return;
    }

    const result = initialList.filter((item) => {
      const displayName = (item.displayName || "").toLowerCase();
      const translateName = (item.translateName || "").toLowerCase();

      return (
        displayName.includes(searchValue) || translateName.includes(searchValue)
      );
    });

    updateList(result);
  };

  /**
   * Categorize labels into well-defined, created, and deleted
   */
  const categorizedLabels = useMemo(() => {
    const wellDefinedLabels = [];
    const createdLabels = [];
    let deletedDashboardsLabel = null;

    list.forEach((item) => {
      if (item.name === DELETED_LABEL) {
        deletedDashboardsLabel = item;
      } else if (!item.isDeletable) {
        wellDefinedLabels.push(item);
      } else {
        createdLabels.push(item);
      }
    });

    return { wellDefinedLabels, createdLabels, deletedDashboardsLabel };
  }, [list]);

  const { wellDefinedLabels, createdLabels, deletedDashboardsLabel } =
    categorizedLabels;

  const renderLabelRow = (item, index) => {
    const isSelected = detectSelection(item);
    const textColor = getThemeProperties("textColor");
    const checkColor = comesFromDashboardArea
      ? textColor || "#099DB2"
      : "#099DB2";

    return (
      <Row
        key={item.id}
        style={{
          textAlign: "left",
          padding: "10px 12px",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          borderRadius: "6px",
        }}
        onClick={() => select(item)}
        className={isSelected ? "disabled-row" : "default-row"}
      >
        <TitleWithIcon
          key={index}
          color={item.color}
          icon={item.icon}
          title={item.translateName || item.displayName}
          prefixClassName="colored-shape-custom"
          titleClassName="title-style"
          comesFromDashboardArea={comesFromDashboardArea}
        />
        <CheckCircleFilled
          style={{
            color: checkColor,
            float: "right",
            marginRight: "5px",
            fontSize: "16px",
            marginTop: "3px",
            opacity: isSelected ? 1 : 0, // Assuming default-row hides it via CSS, but explicit opacity is safer
          }}
        />
      </Row>
    );
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          padding: "4px 4px 12px 4px",
          borderBottom: `1px solid ${getThemeProperties("textColor")}1a`,
          marginBottom: "12px",
          flexShrink: 0,
        }}
      >
        <Text
          style={{
            color: getThemeProperties("textColor"),
            fontWeight: 600,
            fontSize: "16px",
          }}
        >
          {i18n.t("LandingPage.Label.Title")}
        </Text>
      </div>

      <div style={{ marginBottom: "12px", flexShrink: 0 }}>
        <Search
          placeholder={i18n.t("Dashboard.Label.Selector.Search")}
          onChange={onSearch}
          style={{
            borderRadius: "4px",
            overflow: "hidden",
          }}
        />
      </div>

      <div
        className="scroll-labels"
        style={{
          maxHeight: maxHeight,
          flex: maxHeight === "100%" ? 1 : "0 1 auto",
          overflowY: "auto",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: 150,
            }}
          >
            <LoadingOutlined style={{ fontSize: 24 }} />
          </div>
        ) : (
          <>
            {wellDefinedLabels.map(renderLabelRow)}

            {createdLabels.length > 0 && (
              <>
                <Divider style={{ margin: "4px 0px 12px 0px" }} />
                {createdLabels.map(renderLabelRow)}
              </>
            )}

            {deletedDashboardsLabel && (
              <>
                <Divider style={{ margin: "4px 0px 12px 0px" }} />
                {renderLabelRow(deletedDashboardsLabel)}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Selector;
